/**
 * The timeline: episodes as bars on lanes across a stretch of time - the
 * Gantt view of what a rule found, and when.
 *
 * One drawing, used wherever Argus shows history: the triage page (this rule
 * across its entities, or this entity across its rules), and in time the
 * Inbox, the rule drawer and the Backtest room (`todos/argus-roadmap.md`, T1).
 * A bar says what became of its episode by how it is drawn:
 *
 * - **open** - the severity's colour, breathing with the alert glow;
 * - **closed** - the severity's colour, still;
 * - **judged** - the verdict's colour: red confirmed, green not an issue,
 *   grey duplicate (Needs more info stays open, in blue);
 * - **snoozed** - the severity's colour, faint;
 * - **dismissed** - grey;
 * - **unwatched** (its rule was stopped or changed while it went on) - hatched.
 *
 * Drawn from what watchers reported; nothing is computed here (rule 3).
 */

import { html, nothing, type TemplateResult } from 'lit';
import type { ArgusTimelineBar, ArgusTimelineLane } from '../../api/types.ts';
import { VERDICT_WORDS } from './argus-episodes.ts';

const DAY = 86400000;
const SEVERITIES = new Set(['critical', 'watch', 'info']);

function ms(iso: string): number {
  // Watchers write naive UTC in places; the database answers with an offset.
  return Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
}

/** Where along [from, to] a moment falls, as a percentage, clamped. */
export function along(t: number, from: number, to: number): number {
  return Math.max(0, Math.min(100, ((t - from) / Math.max(1, to - from)) * 100));
}

function stamp(t: number): string {
  return new Date(t).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function day(t: number): string {
  return new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

const STATE_WORDS: Record<string, string> = {
  open: 'still open',
  judged: 'judged',
  snoozed: 'muted',
  closed: 'ended',
  dismissed: 'dismissed',
  unwatched: 'no longer watched',
};

export interface TimelineOptions {
  /** The window drawn, in epoch ms. Defaults to `days` back from now. */
  from?: number;
  to?: number;
  days?: number;
  /** The bar to pick out - the episode being looked at. */
  highlight?: number | null;
  /** A lane to pick out - this entity, or this rule. */
  highlightLane?: string | null;
  /** Click a bar. Without it, bars are not buttons. */
  onPick?: (bar: ArgusTimelineBar) => void;
  /** What a quiet lane says. */
  quiet?: string;
}

export function renderTimeline(lanes: readonly ArgusTimelineLane[], opts: TimelineOptions = {}): TemplateResult {
  const now = Date.now();
  const to = opts.to ?? now;
  const from = opts.from ?? to - (opts.days ?? 90) * DAY;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => from + f * (to - from));
  if (!lanes.length) return html`<p class="argus-lfaint">Nothing to draw.</p>`;
  return html`<div class="argus-tl" role="list">
    ${lanes.map((lane) => {
      const on = opts.highlightLane === lane.key;
      return html`<div class="argus-tl-row ${on ? 'is-on' : ''}" role="listitem">
        <span class="argus-tl-name" title=${lane.label}>${lane.label}</span>
        <span class="argus-tl-track">
          ${lane.bars.length
            ? lane.bars.map((bar) => {
                const start = ms(bar.start);
                const end = ms(bar.end ?? bar.last);
                if (end < from || start > to) return nothing;
                const left = along(start, from, to);
                const right = along(end, from, to);
                const severity = SEVERITIES.has(bar.severity) ? bar.severity : 'watch';
                const said = bar.verdict ? VERDICT_WORDS[bar.verdict] ?? bar.verdict : STATE_WORDS[bar.state] ?? bar.state;
                const label = `${lane.label}: ${stamp(start)} - ${bar.end ? stamp(end) : `${stamp(end)}, still going`} · ${said}`;
                const cls = `argus-tl-bar is-${bar.state} is-${severity} ${bar.verdict ? `is-v-${bar.verdict}` : ''} ${bar.id === opts.highlight ? 'is-here' : ''}`;
                const style = `left:${left}%;width:max(3px, ${right - left}%)`;
                return opts.onPick
                  ? html`<button
                      type="button"
                      class=${cls}
                      style=${style}
                      title=${label}
                      aria-label=${label}
                      @click=${() => opts.onPick?.(bar)}
                    ></button>`
                  : html`<span class=${cls} style=${style} title=${label}></span>`;
              })
            : html`<span class="argus-tl-quiet">${opts.quiet ?? 'nothing'}</span>`}
        </span>
      </div>`;
    })}
    <div class="argus-tl-row is-axis" aria-hidden="true">
      <span class="argus-tl-name"></span>
      <span class="argus-tl-track">
        ${ticks.map((t, i) => html`<span class="argus-tl-tick" style=${`left:${i * 25}%`}>${day(t)}</span>`)}
      </span>
    </div>
  </div>`;
}
